import { Table, Tag, Button, Space } from '@/shared/ui/primitives';
import { EyeOutlined, DeleteOutlined } from '@/shared/ui/icons';
import { formatDateTime } from '@/shared/utils/formatters';
import { useTranslation } from 'react-i18next';
import type { ColumnsType, TableProps } from '@/shared/ui/subpath-types';

const AD_TYPE_COLORS: Record<string, string> = {
  ROOM_AD: 'blue',
  ROOM_WANTED_AD: 'orange',
};

interface Props {
  data: any[];
  loading: boolean;
  sortField: string;
  sortDir: string;
  onSort: (field: string) => void;
  onView: (id: number) => void;
  onDelete: (item: any) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export default function ChatRoomTable({ data, loading, sortField, sortDir, onSort, onView, onDelete, page, pageSize, total, onPageChange }: Props) {
  const { t } = useTranslation();

  const columns: ColumnsType<any> = [
    { title: t('common.id'), dataIndex: 'id', sorter: true, width: 80 },
    {
      title: t('chat.adType'), dataIndex: 'adType', sorter: true, width: 140,
      render: (v: string) => <Tag color={AD_TYPE_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: t('chat.ad'), dataIndex: 'ad',
      render: (_: any, record: any) =>
        record.roomAdTitle ? `Room #${record.roomAdId}: ${record.roomAdTitle}` :
        record.roomWantedAdTitle ? `Wanted #${record.roomWantedAdId}: ${record.roomWantedAdTitle}` : '—',
    },
    {
      title: t('chat.owner'),
      render: (_: any, record: any) => record.ownerUserName ? `${record.ownerUserName} (#${record.ownerUserId})` : '—',
    },
    {
      title: t('chat.starter'),
      render: (_: any, record: any) => record.starterUserName ? `${record.starterUserName} (#${record.starterUserId})` : '—',
    },
    {
      title: t('chat.lastMessage'), dataIndex: 'lastMessageAt', sorter: true,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('common.createdAt'), dataIndex: 'createdAt', sorter: true,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('common.actions'), width: 100,
      render: (_: any, record: any) => (
        <Space>
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => onView(record.id)} />
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} />
        </Space>
      ),
    },
  ];

  const handleChange: TableProps['onChange'] = (_pagination, _filters, sorter: any) => {
    if (sorter?.field) onSort(sorter.field);
  };

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      onChange={handleChange}
      sortDirections={['ascend', 'descend']}
      showSorterTooltip={false}
      pagination={{
        current: page + 1,
        pageSize,
        total,
        showTotal: (total) => `${total} items`,
        showSizeChanger: false,
        onChange: (p) => onPageChange(p - 1),
      }}
    />
  );
}
